import { Component, computed, effect, inject, signal } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatIconButton } from '@angular/material/button';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldModule } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Address } from '../../../../api/app-types/address.app';
import { ContactPoint, ContactPointCategory } from '../../../../api/app-types/contact-point.app';
import { InstitutionExtended } from '../../../../api/app-types/institution.app';
import { INSTITUTION_SERVICE } from '../../../../api/services/institution.service';
import { LvrRedirectNav } from '../../../../core/classes/redirect-nav.class';
import { AddressCompletion } from '../../../../core/components/address-completion/address-completion';
import { LvrContactPoint } from '../../../../core/components/contact-point/contact-point';
import { LvrDefaultActions } from '../../../../core/components/default-actions/default.actions';
import { LvrField } from '../../../../core/components/field/field';
import { LvrInputDirective } from '../../../../core/directives/lvr-input.directive';
import { hasError } from '../../../../shared/helpers/has-error.func';
import { wrapPromiseWithNotify } from '../../../../shared/helpers/notify.func';
import { NOTIFICATION_SERVICE } from '../../../../shared/notification/notification';
import { LvrPaddingDirective } from '../../../../shared/padding/padding.directive';
import { PaginatedResponse } from '../../../../shared/pagination/paginated-response';
import { LvrAppendAsteriskPipe } from '../../../../shared/pipes/append-asterisk.pipe';
import { LvrAutoSaveDirective } from '../../../../shared/storage/auto-save.directive';
import { FORM_KEYS } from '../../../../shared/storage/form-keys.type';
import { FormPersistenceService } from '../../../../shared/storage/form-persistence.service';
import { SessionManagerService } from '../../../../shared/storage/session-manager.service';
import { LvrRestoreActions } from '../../../../shared/storage/restore-actions';
import { ImmediateErrorStateMatcher } from '../../../../shared/validators/immediate-error-state-matcher';
import { LvrButton } from '../../../../core/components/lvr-button/lvr-button';
import { ENVIRONMENT } from '../../../../../environments/environment.interface';
import { hasPermission } from '../../../../shared/permissions/permission.func';
import { isContactPointComplete } from '../../../../shared/helpers/is-contact-point-complete.func';
import { RECORD_MANAGER } from '../../../../core/services/record.provide';

@Component({
	selector: 'lvr-institution-details-edit',
	templateUrl: './institution-details-edit.html',
	imports: [
		LvrPaddingDirective,
		ReactiveFormsModule,
		TranslocoPipe,
		LvrField,
		MatFormFieldModule,
		LvrInputDirective,
		MatInput,
		FormsModule,
		LvrAppendAsteriskPipe,
		AddressCompletion,
		MatRadioGroup,
		MatRadioButton,
		LvrAutoSaveDirective,
		LvrRestoreActions,
		LvrContactPoint,
		MatIcon,
		MatIconButton,
		LvrDefaultActions,
		LvrButton,
	],
	providers: [
		{
			provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
			useValue: { subscriptSizing: 'dynamic' },
		},
	],
})
export class InstitutionDetailsEdit extends LvrRedirectNav {
	public readonly MIN_COMMUNICATION_WAYS = 1;
	public readonly MAX_COMMUNICATION_WAYS = 5;
	private readonly api = inject(INSTITUTION_SERVICE);
	private readonly fb = inject(FormBuilder);
	private readonly persistentStorage = inject(FormPersistenceService);
	private readonly ntfy = inject(NOTIFICATION_SERVICE);
	private readonly session = inject(SessionManagerService);
	private readonly transloco = inject(TranslocoService);
	public readonly env = inject(ENVIRONMENT);
	private readonly recordManager = inject(RECORD_MANAGER);
	private readonly institution = this.resolveDataAsSignal<InstitutionExtended>('institution');
	public readonly contactPointTypePage =
		this.resolveDataAsSignal<PaginatedResponse<ContactPointCategory>>('contactPointTypes');
	public readonly contactPointTypes = computed(() => this.contactPointTypePage()?.entities ?? []);
	public readonly communicationWays = computed(() => this.institution()?.contactPoints ?? []);
	public readonly communicationWayCount = signal(1);
	public readonly communicationWayCountArrayKeys = computed(() => Array(this.communicationWayCount()));
	private readonly branchRegex = new RegExp(this.env.branchNameRegexStr);
	private title = signal<string>('');

	public readonly saving = signal(false);
	public readonly draftId = FORM_KEYS.CREATE_INSTITUTION;
	public readonly hasError = hasError;
	public readonly matcher = new ImmediateErrorStateMatcher();
	public readonly canEditInstitutionsPermission = hasPermission(this.session.permissions, 'Institutions.Edit');
	public readonly form = this.fb.group({
		name: [this.institution()?.['name'] ?? '', [Validators.required, Validators.pattern(this.branchRegex)]],
		address: [this.institution()?.['address']],
		industryName: [this.institution()?.['industryName']],
		employmentRequired: [
			this.institution()?.employmentRequired !== undefined ? this.institution()?.employmentRequired : undefined,
		], // DE: Beschäftigungspflichtig / Nicht Beschäftigungspflichtig
		communicationWays: this.fb.control<ContactPoint[]>(this.communicationWays(), {
			validators: [Validators.max(5)],
		}),
		url: [this.institution()?.url, []],
	});

	public readonly editingId = computed(() => this.institution()?.id);
	public readonly checkDuplicatedAddress = signal(true);

	public duplicatedAddressCheckAsync = async (address: Address, excludeId?: number) => {
		const street = (address.streetAndHouseNumber ?? '').trim();
		const postalCity = (address.postalCodeAndCity ?? '').trim();
		if (!street || !postalCity) return undefined;
		return await this.api.isAddressUnique(street, postalCity, excludeId);
	};

	public constructor() {
		super();
		effect(() => {
			const count = this.communicationWays().length;
			this.communicationWayCount.set(count);
		});

		effect(() => {
			const institution = this.institution();
			if (!institution) return;
			if (institution.name !== this.title()) {
				this.title.set(institution.name);
				this.recordManager.updateRecordTitleOfActiveRecord(this.title());
			}
		});
	}

	public formCommunicationWays() {
		const vals = this.form.get('communicationWays')?.value ?? [];
		return vals;
	}

	public async save() {
		if (this.saving()) return;

		if (this.form.invalid) {
			this.form.markAllAsTouched();
			return;
		}

		this.saving.set(true);

		try {
			const value = this.form.value;
			const i = this.institution()!;
			const body: InstitutionExtended = {
				...this.institution()!,
				name: value.name ?? i.name,
				url: value.url ?? i.url,
				employmentRequired: value.employmentRequired ?? i.employmentRequired,
				additionalName: i.additionalName,
				id: i.id,
			};

			// if there is an address on the form value use it, otherwise use the one from i if there is any
			if (value.address) {
				body.address = value.address;
			} else if (i.address) {
				body.address = i.address;
			} else {
				body.address = undefined;
			}

			if (value.industryName) {
				body.industryName = value.industryName;
			} else if (i.industryName) {
				body.industryName = i.industryName;
			} else {
				body.industryName = undefined;
			}

			// if there are communication ways check if they have an id (larger than 0) and update them on the body with new fields. If there are new ones (undefined id or -1) add them to the body
			// Filter empty new contact points
			const rawCws = (value.communicationWays ?? []).filter(isContactPointComplete);
			const updatedCws: ContactPoint[] = [];
			const newCws: ContactPoint[] = [];

			for (const cp of rawCws) {
				if (cp.id && cp.id > 0) {
					// existing contact point, update it
					const existing = i.contactPoints.find((e) => e.id === cp.id);
					if (existing) {
						updatedCws.push({
							...existing,
							contactValue: cp.contactValue ?? existing.contactValue,
							categoryContactPointId: cp.categoryContactPointId!,
							preferredWayOfContact: cp.preferredWayOfContact ?? existing.preferredWayOfContact,
							remark: cp.remark ?? existing.remark,
						});
					}
				} else {
					// new contact point (not empty), add it
					newCws.push({
						personId: -1,
						personTypeId: -1,
						categoryContactPointId: cp.categoryContactPointId,
						contactValue: cp.contactValue ?? '',
						preferredWayOfContact: cp.preferredWayOfContact ?? false,
						remark: cp.remark ?? '',
					});
				}
			}

			body.contactPoints = [...updatedCws, ...newCws];

			const id = await wrapPromiseWithNotify(
				this.api.update(body),
				this.transloco.translate('base.notification_message.institution_updated'),
				this.ntfy,
				this.transloco.translate('base.notification_message.institution_update_error'),
			);
			this.persistentStorage.deleteDraft('institution', this.draftId);
			await this.navigateAfterFinished(['/desk/institution', id]);
		} finally {
			this.saving.set(false);
		}
	}

	public cancel() {
		this.persistentStorage.deleteDraft('institution', this.draftId);
		this.navigateAfterFinished(['/desk/institution', this.institution()?.id]);
	}

	public updateValues(data: { valid: boolean; value: ContactPoint }, index: number) {
		const ctrl = this.form.get('communicationWays');
		if (!ctrl) return;
		if (data.valid) {
			const values = ctrl.value ?? [];
			if (index !== -1) {
				values[index] = data.value;
			} else {
				values.push(data.value);
			}
			ctrl.setValue(values);
		} else {
			const values = ctrl.value ?? [];
			if (index !== -1) {
				values.splice(index, 1);
			}
			ctrl.setValue(values);
		}
	}

	public addCommunicationWay() {
		const vals = this.form.get('communicationWays')?.value ?? [];
		vals.push({
			id: -1,
			personId: -1,
			personTypeId: -1,
			categoryContactPointId: -1,
			contactValue: '',
			preferredWayOfContact: false,
			remark: '',
		});
		this.form.get('communicationWays')?.setValue(vals);
		this.communicationWayCount.set(this.communicationWayCount() + 1);
	}

	// Remove them based on the index
	public removeCommunicationWayAt(index: number) {
		const ctrl = this.form.get('communicationWays');
		if (!ctrl) return;
		const values = ctrl.value ?? [];
		if (index >= 0 && index < values.length) {
			values.splice(index, 1);
			ctrl.setValue(values);
			this.communicationWayCount.set(values.length);
		}
	}
}
